import { useEffect, useState } from 'react';
import {
  collection, doc, addDoc, updateDoc, deleteDoc,
  onSnapshot, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.jsx';

// Skill categories: tags assigned by the customer when submitting a ticket.
// Each skill maps to one or more teams that can handle it. Each skill can ALSO
// carry a per-skill AI triage playbook (AI-PHASE-1, Task #100) — admin-curated
// markdown injected into the system prompt of the customer-side AI chat.

// Default playbook template loaded for new skills. Spec §5.3 — admins write the
// playbook as plain markdown; the customer AI uses it to know what to ask.
const PLAYBOOK_TEMPLATE = `# Common issues to ask about
- What exactly is happening? (symptom)
- When did it start?
- Has anything changed recently? (new install, weather, power outage)
- Is it constant or intermittent?

# Self-checks to suggest
- Is the unit powered on? (Check the visible switch only — never open panels)
- Are any error codes visible on the display?
- Has the breaker tripped at the main board?

# Always escalate if
- The customer reports any smoke, smell of gas, water on electrical, or visible damage
- The customer says it's urgent or for a critical environment (medical, food storage, server room)
- The customer is unsure or uncomfortable doing any check
`;

export default function Skills() {
  const { claims } = useAuth();
  const vendorId = claims?.vendor_id;

  const [skills, setSkills] = useState(null);
  const [teams, setTeams] = useState([]);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!vendorId) return;
    const unsubSkills = onSnapshot(
      collection(db, 'vendors', vendorId, 'skillCategories'),
      (snap) => setSkills(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => setError(err.message),
    );
    const unsubTeams = onSnapshot(
      collection(db, 'vendors', vendorId, 'teams'),
      (snap) => setTeams(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => setError(err.message),
    );
    return () => { unsubSkills(); unsubTeams(); };
  }, [vendorId]);

  async function createSkill(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true); setError(null);
    try {
      await addDoc(collection(db, 'vendors', vendorId, 'skillCategories'), {
        name: name.trim(),
        description: '',
        teamIds: [],
        // AI-PHASE-1: every new skill ships with a starter playbook so the
        // AI triage chat has something useful to ask from day one.
        playbook: PLAYBOOK_TEMPLATE,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setName('');
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  async function removeSkill(skillId) {
    if (!confirm('Delete this skill category?')) return;
    try { await deleteDoc(doc(db, 'vendors', vendorId, 'skillCategories', skillId)); }
    catch (err) { setError(err.message); }
  }

  async function toggleTeamForSkill(skill, teamId) {
    const inSkill = (skill.teamIds || []).includes(teamId);
    const next = inSkill
      ? skill.teamIds.filter((t) => t !== teamId)
      : [...(skill.teamIds || []), teamId];
    try {
      await updateDoc(doc(db, 'vendors', vendorId, 'skillCategories', skill.id), {
        teamIds: next, updatedAt: serverTimestamp(),
      });
    } catch (err) { setError(err.message); }
  }

  return (
    <>
      <h2>Skill Categories</h2>
      <p style={{ color: 'var(--muted)', marginTop: -8 }}>
        Customers pick a skill category when submitting a request. Map each skill to the team(s) that should handle it.
      </p>

      <form onSubmit={createSkill} style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        <input
          type="text" placeholder="New skill (e.g. Refrigeration, PLC, Conveyor)"
          value={name} onChange={(e) => setName(e.target.value)}
          style={{ flex: 1, padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}
        />
        <button type="submit" disabled={busy || !name.trim()} className="btn-primary">
          {busy ? 'Creating…' : 'Add skill'}
        </button>
      </form>

      {error && <div className="error">{error}</div>}

      {skills === null && <div className="placeholder">Loading…</div>}
      {skills && skills.length === 0 && (
        <div className="placeholder">No skills yet. Create your first one above.</div>
      )}
      {skills && skills.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {skills.map((skill) => (
            <div key={skill.id} className="card">
              <div className="card-header">
                <h3>{skill.name}</h3>
                <button onClick={() => removeSkill(skill.id)} className="btn-ghost-danger">Delete</button>
              </div>
              <div style={{ marginTop: 12 }}>
                <strong>Handled by:</strong>
                {teams.length === 0 ? (
                  <div className="placeholder" style={{ marginTop: 8 }}>
                    No teams created yet. Add a team first.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {teams.map((team) => {
                      const active = (skill.teamIds || []).includes(team.id);
                      return (
                        <button key={team.id}
                          onClick={() => toggleTeamForSkill(skill, team.id)}
                          className={active ? 'btn-chip-active' : 'btn-chip'}>
                          {active ? '✓ ' : '+ '}{team.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* AI-PHASE-1: per-skill AI triage playbook editor */}
              <PlaybookEditor vendorId={vendorId} skill={skill} />
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ============================================================================
// PlaybookEditor — per-skill markdown playbook for the customer-side AI triage.
// AI-PHASE-1 (Task #100). Lives on each skill card.
// ============================================================================

function PlaybookEditor({ vendorId, skill }) {
  const [open, setOpen] = useState(false);
  // Local draft so typing doesn't re-render the whole card on every keystroke
  // AND so we only write to Firestore when the admin hits Save.
  const [draft, setDraft] = useState(skill.playbook || PLAYBOOK_TEMPLATE);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(0);

  // If the underlying skill.playbook changes from elsewhere (e.g. another tab),
  // sync the draft — but only when the editor is closed so we don't clobber
  // in-progress edits.
  useEffect(() => {
    if (!open) setDraft(skill.playbook || PLAYBOOK_TEMPLATE);
  }, [skill.playbook, open]);

  async function save() {
    setBusy(true);
    try {
      await updateDoc(doc(db, 'vendors', vendorId, 'skillCategories', skill.id), {
        playbook: draft.slice(0, 8000),  // generous cap — playbooks should stay small per spec
        updatedAt: serverTimestamp(),
      });
      setSavedAt(Date.now());
    } finally {
      setBusy(false);
    }
  }

  function resetToTemplate() {
    if (!confirm('Replace this playbook with the default template? Your current playbook will be lost.')) return;
    setDraft(PLAYBOOK_TEMPLATE);
  }

  const hasContent = !!(skill.playbook && skill.playbook.trim());
  const wordCount = draft.trim().split(/\s+/).filter(Boolean).length;

  return (
    <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          padding: 0, fontSize: 14, fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 6,
        }}
      >
        <span>{open ? '▾' : '▸'}</span>
        <span>AI triage playbook</span>
        <span style={{
          fontSize: 11, fontWeight: 600,
          padding: '2px 7px', borderRadius: 999,
          background: hasContent ? '#dcfce7' : '#fef3c7',
          color: hasContent ? '#166534' : '#92400e',
        }}>
          {hasContent ? 'Configured' : 'Default'}
        </span>
      </button>

      {open && (
        <div style={{ marginTop: 10 }}>
          <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 0 }}>
            Plain-language guidance for the AI when triaging <strong>{skill.name}</strong> tickets.
            Tell it what to ask, what self-checks are safe to suggest, and when to escalate immediately.
            Markdown supported.
          </p>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={14}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 13,
              lineHeight: 1.5,
              resize: 'vertical',
            }}
          />
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            marginTop: 8, flexWrap: 'wrap',
          }}>
            <button
              type="button"
              onClick={save}
              disabled={busy || draft === (skill.playbook || PLAYBOOK_TEMPLATE)}
              className="btn-primary">
              {busy ? 'Saving...' : 'Save playbook'}
            </button>
            <button
              type="button"
              onClick={resetToTemplate}
              disabled={busy}
              className="btn-ghost">
              Reset to template
            </button>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>
              {wordCount} words
              {savedAt > 0 && Date.now() - savedAt < 5000 && (
                <> · <span style={{ color: '#166534' }}>Saved</span></>
              )}
            </span>
          </div>
          <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8, marginBottom: 0 }}>
            Safety reminders are hardcoded — the AI will never suggest opening electrical panels,
            handling refrigerant, or bypassing interlocks, regardless of what's in your playbook.
          </p>
        </div>
      )}
    </div>
  );
}
